import { z } from 'zod';
import { activeTotalsSchema } from '@/api/listTotals';
import { paginationMetaSchema } from '@/api/pagination';
import { budgetTotalsSchema } from './marketPromotion.types';

const numberList = z.preprocess(
  (value) => (value === undefined ? undefined : Array.isArray(value) ? value : [value]),
  z.array(z.coerce.number()),
);

export const marketProjectSchema = z.object({
  id: z.coerce.number(),
  legacyId: z.coerce.number().nullable(),
  code: z.string().nullable(),
  name: z.string().nullable(),
  classId: z.coerce.number().nullable(),
  className: z.string().nullable(),
  startDate: z.string().nullable(),
  deadlineDate: z.string().nullable(),
  comments: z.string().nullable(),
  isActive: z.boolean(),
});

export const marketProjectResponseSchema = z.object({
  data: z.array(marketProjectSchema),
  meta: paginationMetaSchema.extend({
    totals: activeTotalsSchema.extend({ beforeDeadline: z.number() }),
  }),
});

export const marketProjectSearchSchema = z.object({
  search: z.string().optional(),
  isActive: z.boolean().optional(),
});

export const marketProjectFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(300, 'Name is too long'),
  code: z.string().trim().max(100, 'Code is too long'),
  classId: z.coerce.number().optional(),
  startDate: z.string().trim(),
  deadlineDate: z.string().trim(),
  comments: z.string().trim().max(2000, 'Comment is too long'),
  isActive: z.boolean(),
});

export const donationBudgetSchema = z.object({
  id: z.coerce.number(),
  legacyId: z.coerce.number().nullable(),
  userId: z.coerce.number().nullable(),
  userName: z.string().nullable(),
  username: z.string().nullable(),
  nickname: z.string().nullable(),
  mobile: z.string().nullable(),
  contactNo: z.string().nullable(),
  designation: z.string().nullable(),
  department: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  promoItemId: z.coerce.number().nullable(),
  itemName: z.string().nullable(),
  itemCode: z.string().nullable(),
  budgetAmount: z.number(),
  disburseAmount: z.number(),
  budgetDate: z.string().nullable(),
  targetDate: z.string().nullable(),
  startDate: z.string().nullable(),
  comments: z.string().nullable(),
});

export const donationBudgetResponseSchema = z.object({
  data: z.array(donationBudgetSchema),
  meta: paginationMetaSchema.extend({ totals: budgetTotalsSchema }),
});

export const donationBudgetSearchSchema = z.object({
  userId: z.coerce.number().optional(),
  promoItemId: z.coerce.number().optional(),
});

export const donationBudgetFormSchema = z.object({
  userId: z.coerce.number().min(1, 'Pick an RSM'),
  promoItemId: z.coerce.number().min(1, 'Pick an item'),
  budgetAmount: z.string().trim().min(1, 'A budget is required'),
  disburseAmount: z.string().trim(),
  budgetDate: z.string().trim(),
  targetDate: z.string().trim(),
  startDate: z.string().trim(),
  comments: z.string().trim().max(2000, 'Comment is too long'),
});

export const donationBatchSchema = z.object({
  batchId: z.coerce.number(),
  legacyId: z.coerce.number().nullable(),
  name: z.string().nullable(),
  publishDate: z.string().nullable(),
  publishedBy: z.string().nullable(),
  userId: z.coerce.number().nullable(),
  promoItemId: z.coerce.number().nullable(),
  itemName: z.string().nullable(),
  promoItemQty: z.number(),
  trnDateFrom: z.string().nullable(),
  trnDateTo: z.string().nullable(),
  isConfirm: z.boolean().nullable(),
  confirmDate: z.string().nullable(),
  confirmedBy: z.string().nullable(),
  salesComment: z.string().nullable(),
  isDisburse: z.boolean(),
  disburseDate: z.string().nullable(),
  disbursedBy: z.string().nullable(),
  accountsComment: z.string().nullable(),
  publishComment: z.string().nullable(),
  stage: z.string(),
});

export const donationBatchResponseSchema = z.object({
  data: z.array(donationBatchSchema),
  meta: paginationMetaSchema,
});

export const donationBatchSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  batchId: z.coerce.number().optional(),
  promoItemId: z.coerce.number().optional(),
  userId: z.coerce.number().optional(),
});

export const donationBatchMemberSchema = z.object({
  userId: z.coerce.number(),
  username: z.string().nullable(),
  fullName: z.string().nullable(),
  hrEmpId: z.string().nullable(),
  mobile: z.string().nullable(),
  reportingName: z.string().nullable(),
  accountNumber: z.string().nullable(),
  designationName: z.string().nullable(),
  departmentName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  promoItemId: z.coerce.number().nullable(),
  itemName: z.string().nullable(),
  donationAmount: z.number(),
  donationCount: z.number(),
});

export const donationBatchMemberListSchema = z.array(donationBatchMemberSchema);

export const donationCandidateSchema = z.object({
  userId: z.coerce.number(),
  fullName: z.string().nullable(),
  hrEmpId: z.string().nullable(),
  designation: z.string().nullable(),
  promoItemId: z.coerce.number().nullable(),
  itemName: z.string().nullable(),
  donationCount: z.number(),
  donationAmount: z.number(),
});

export const donationCandidateListSchema = z.array(donationCandidateSchema);

export const donationRequisitionSchema = z.object({
  id: z.coerce.number(),
  legacyId: z.coerce.number().nullable(),
  specimenId: z.coerce.number().nullable(),
  specimenName: z.string().nullable(),
  specimenCode: z.string().nullable(),
  bookTypeNo: z.coerce.number().nullable(),
  bookTypeName: z.string().nullable(),
  instituteId: z.coerce.number().nullable(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteEiin: z.string().nullable(),
  instituteType: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  teacherName: z.string().nullable(),
  teacherPhone: z.string().nullable(),
  budgetAmount: z.number(),
  targetSaleQty: z.number(),
  studentQty: z.coerce.number().nullable(),
  isChecked: z.boolean(),
  isApproved: z.boolean(),
  isAudit: z.boolean(),
  isActive: z.boolean(),
  enteredBy: z.string().nullable(),
  enteredAt: z.string().nullable(),
});

export const donationRequisitionResponseSchema = z.object({
  data: z.array(donationRequisitionSchema),
  meta: paginationMetaSchema,
});

export const donationRequisitionSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  thanaId: z.coerce.number().optional(),
  instituteId: z.coerce.number().optional(),
  bookTypeNo: z.coerce.number().optional(),
  instituteTypeNo: z.coerce.number().optional(),
  isActive: z.boolean().optional(),
});

export const donationRequisitionFormSchema = z.object({
  instituteId: z.coerce.number().optional(),
  specimenId: z.coerce.number().optional(),
  bookTypeNo: z.coerce.number().optional(),
  instituteTypeNo: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
  teacherName: z.string().trim().max(300, 'Name is too long'),
  teacherPhone: z.string().trim().max(20, 'Phone is too long'),
  budgetAmount: z.string().trim().min(1, 'A budget is required'),
  targetSaleQty: z.string().trim(),
  studentQty: z.string().trim(),
});

export const requisitionInstituteSchema = z.object({
  instituteId: z.coerce.number(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string(),
  instituteEiin: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
});

export const requisitionInstituteListSchema = z.array(requisitionInstituteSchema);

export const approvalTotalSchema = z.object({
  paymentType: z.string(),
  donations: z.number(),
  claimed: z.number(),
  approved: z.number(),
});

export const approvalTotalListSchema = z.array(approvalTotalSchema);

export const approvalReportSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  enteredBy: z.coerce.number().optional(),
  enteredMobile: z.string().optional(),
  instituteId: z.coerce.number().optional(),
  instituteEiin: z.string().optional(),
  teacherMobile: z.string().optional(),
  promoItemIds: numberList.optional(),
  approveTypeNos: numberList.optional(),
});

export type MarketProject = z.infer<typeof marketProjectSchema>;
export type MarketProjectResponse = z.infer<typeof marketProjectResponseSchema>;
export type MarketProjectSearch = z.infer<typeof marketProjectSearchSchema>;
export type MarketProjectFormValues = z.infer<typeof marketProjectFormSchema>;

export type DonationBudget = z.infer<typeof donationBudgetSchema>;
export type DonationBudgetResponse = z.infer<typeof donationBudgetResponseSchema>;
export type DonationBudgetSearch = z.infer<typeof donationBudgetSearchSchema>;
export type DonationBudgetFormValues = z.infer<typeof donationBudgetFormSchema>;

export type DonationBatch = z.infer<typeof donationBatchSchema>;
export type DonationBatchResponse = z.infer<typeof donationBatchResponseSchema>;
export type DonationBatchSearch = z.infer<typeof donationBatchSearchSchema>;
export type DonationBatchMember = z.infer<typeof donationBatchMemberSchema>;
export type DonationCandidate = z.infer<typeof donationCandidateSchema>;

export type DonationRequisition = z.infer<typeof donationRequisitionSchema>;
export type DonationRequisitionResponse = z.infer<typeof donationRequisitionResponseSchema>;
export type DonationRequisitionSearch = z.infer<typeof donationRequisitionSearchSchema>;
export type DonationRequisitionFormValues = z.infer<typeof donationRequisitionFormSchema>;

export type RequisitionInstitute = z.infer<typeof requisitionInstituteSchema>;
export type ApprovalTotal = z.infer<typeof approvalTotalSchema>;
export type ApprovalReportSearch = z.infer<typeof approvalReportSearchSchema>;

export interface MarketProjectPayload {
  name: string;
  code?: string;
  classId?: number;
  startDate?: string;
  deadlineDate?: string;
  comments?: string;
  isActive?: boolean;
}

export interface DonationBudgetPayload {
  userId: number;
  promoItemId: number;
  budgetAmount: number;
  disburseAmount?: number;
  budgetDate?: string;
  targetDate?: string;
  startDate?: string;
  comments?: string;
}

export interface DonationRequisitionPayload {
  specimenId?: number;
  bookTypeNo?: number;
  instituteTypeNo?: number;
  thanaId?: number;
  instituteId?: number;
  teacherName?: string;
  teacherPhone?: string;
  budgetAmount: number;
  targetSaleQty?: number;
  studentQty?: number;
}

export interface PublishDonationPayload {
  userIds: number[];
  dateFrom: string;
  dateTo: string;
  promoItemId: number;
  amount: number;
  comment?: string;
}
